import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, money, fmtDate, today, ApiError } from '../api'
import { Page } from '../App'
import { Card, Alert, Spinner, Empty, Stat } from '../components/ui'

interface Row {
  id: string
  invoice_id: string | null
  invoice_number: string | null
  client_name: string
  client_display_name: string | null
  receipt_number: string | null
  paid_on: string
  amount: number
  tds_amount: number
  currency: string
  method: string
  reference_utr: string | null
}

export default function Payments() {
  const [rows, setRows] = useState<Row[]>([])
  const [totals, setTotals] = useState<{ received: number; tds: number } | null>(null)
  const [from, setFrom] = useState(today().slice(0, 4) + '-04-01')
  const [to, setTo] = useState(today())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    setLoading(true)
    api
      .get<{ payments: Row[]; totals: { received: number; tds: number } }>(
        `/payments?from=${from}&to=${to}`,
      )
      .then((r) => {
        setRows(r.payments)
        setTotals(r.totals)
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false))
  }, [from, to])

  return (
    <Page title="Payments" subtitle="Everything received, and what the client withheld as TDS">
      <Alert kind="error">{error}</Alert>

      <div className="inline" style={{ marginBottom: 14, maxWidth: 420 }}>
        <div>
          <label className="small muted">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="small muted">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {totals && (
        <div className="grid c3" style={{ marginBottom: 16 }}>
          <Stat label="Received" value={money(totals.received)} tone="ok" />
          <Stat label="TDS deducted by clients" value={money(totals.tds)} note="Reconcile against Form 26AS" />
          <Stat label="Gross settled" value={money(totals.received + totals.tds)} />
        </div>
      )}

      <Card tight>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty icon="₹" title="No payments in this period" />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Receipt</th>
                  <th>Client</th>
                  <th>Invoice</th>
                  <th>Method</th>
                  <th>Reference / UTR</th>
                  <th className="r">TDS</th>
                  <th className="r">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    className={p.invoice_id ? 'clickable' : ''}
                    onClick={() => p.invoice_id && navigate(`/invoices/${p.invoice_id}`)}
                  >
                    <td className="nowrap small">{fmtDate(p.paid_on)}</td>
                    <td className="mono small">{p.receipt_number ?? '—'}</td>
                    <td>{p.client_display_name || p.client_name}</td>
                    <td className="mono small">{p.invoice_number ?? <span className="muted">On account</span>}</td>
                    <td className="small">{p.method.toUpperCase()}</td>
                    <td className="mono small">{p.reference_utr ?? '—'}</td>
                    <td className="num">{p.tds_amount ? money(p.tds_amount, p.currency) : '—'}</td>
                    <td className="num">
                      <strong>{money(p.amount, p.currency)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Page>
  )
}
